import React, { ReactElement, memo } from 'react'
import FontelloIcon from '@sport1/react-elements/FontelloIcon'
import Grid from '@sport1/react-elements/Grid'
import Touchable from '@sport1/react-elements/Touchable'
import { TestID } from '../../../utils/PropTypesDefinitions'
import Image from '../../../BaseComponents/Image'
import Tonal from '../../../BaseComponents/Tonal'
import { TeaserCaption } from '../Components'
import BasicText from '../../../Theme/Fonts/BasicText'
import LinkProps from '../../../Link/LinkProps'

export type ImageProps = {
    /** url to the image */
    url: string
    /** img.alt property, field for seo, optional */
    alt?: string
    /** img.title property, optional */
    title?: string
}

export type ArticleGalleryTeaserProps = {
    /** static width for the gallery */
    width?: number | string
    /** href string to navigate to the provided url (web only) */
    href?: string
    /**
     * callback when the user pressed on the component
     *
     * @TJS-type function
     */
    onPress?: () => void
    /** imageProps must contain url */
    images: ImageProps[]
    /** label text, total number of images in the gallery */
    numberOfImages: number
    /** caption text under the gallery teaser */
    caption?: string
} & LinkProps &
    TestID

const ArticleGalleryTeaser = ({
    width = '100%',
    href,
    onPress,
    images,
    numberOfImages,
    caption,
    testID,
    webRel,
}: ArticleGalleryTeaserProps): ReactElement => (
    <Grid noExpand horizontal width={width} testID={testID} backgroundColor="pearl">
        <Touchable onPress={onPress} href={href} expand webRel={webRel}>
            <Grid noExpand>
                <Grid horizontal noExpand overflow="hidden" borderRadius={4} flexWrap="wrap">
                    <Grid noExpand position="relative" width="50%">
                        <Image
                            size="M"
                            imageUrl={images && images[0]?.url}
                            alt={images && images[0]?.alt}
                            title={images && images[0]?.title}
                        />
                        <Tonal />
                        <Grid zIndex={2} position="absolute" width="100%" height="100%" center>
                            <FontelloIcon
                                name="gallery-active"
                                color="WHITE"
                                fontSize={[22, 34, 50]}
                            />
                        </Grid>
                    </Grid>
                    <Grid noExpand position="relative" width="50%">
                        <Image
                            size="M"
                            imageUrl={images && images[1].url}
                            alt={images && images[1]?.alt}
                            title={images && images[1]?.title}
                        />
                    </Grid>
                    <Grid noExpand position="relative" width="50%">
                        <Image
                            size="M"
                            imageUrl={images && images[2]?.url}
                            alt={images && images[2]?.alt}
                            title={images && images[2]?.title}
                        />
                    </Grid>
                    <Grid noExpand position="relative" width="50%">
                        <Image
                            size="M"
                            imageUrl={images && images[3]?.url}
                            alt={images && images[3]?.alt}
                            title={images && images[3]?.title}
                        />
                        <Tonal />
                        {numberOfImages - 4 > 0 ? (
                            <Grid zIndex={2} position="absolute" width="100%" height="100%" center>
                                <BasicText
                                    color="WHITE"
                                    fontSize={['FS30', 'FS30', 'FS48']}
                                    lineHeight={['LH34', 'LH34', 'LH48']}
                                    bold
                                >
                                    {`+${numberOfImages - 4}`}
                                </BasicText>
                            </Grid>
                        ) : null}
                    </Grid>
                </Grid>
                {caption ? <TeaserCaption caption={caption} /> : null}
            </Grid>
        </Touchable>
    </Grid>
)

export default memo(ArticleGalleryTeaser)
